import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FormService } from './form.service';

import { IFormModel } from '../dynamic-form.interface';
import { VisibilityFunctionType } from '../model';
import { getTypedControls, TypedControl } from '../utils/form-preparing';

/**
 * A service that determines the visibility of controls on a dynamic form
 */
@UntilDestroy()
@Injectable()
export class VisibilityService {
  /** Map of the controls with their values */
  private readonly controlsAndValues: Map<
    TypedControl,
    IFormModel[keyof IFormModel]
  > = new Map();

  /** Set of names of pages/fields/controls, that should be hidden by condition */
  private readonly hiddenControlsNamesByFunctionSubject: BehaviorSubject<
    Set<string>
  > = new BehaviorSubject(new Set());

  /** Function by which the visible fields will be determined */
  private visibilityFunction: VisibilityFunctionType;

  /** Function by which the visible fields will be determined */
  set visibilityFunc(conditionFunc: VisibilityFunctionType) {
    this.visibilityFunction = conditionFunc;
  }

  constructor(private readonly formService: FormService) {
    this.subscribeToDocChange();
    this.subscribeToValuesChanges();
  }

  /**
   * Clear service state
   */
  private clear(): void {
    this.controlsAndValues.clear();
    this.hiddenControlsNamesByFunctionSubject.next(new Set());
  }

  private subscribeToDocChange(): void {
    this.formService.typedForm$
      .pipe(untilDestroyed(this))
      .subscribe(typedForm => {
        this.clear();

        if (!typedForm) {
          return;
        }

        const controls = getTypedControls(typedForm);

        controls.forEach(control => {
          if ('value' in control && control.value !== undefined) {
            this.handleControl(control, control.value);
          }
        });
      });
  }

  private subscribeToValuesChanges(): void {
    this.formService.valueChanges$
      .pipe(untilDestroyed(this))
      .subscribe(newModel => {
        this.updateControlValues(newModel);
        this.recalculateVisibilities(this.controlsAndValues);
      });
  }

  /**
   * Update(make new) hidden and disabled set
   *
   * @param controlAndValues - some controls metadata and their current value
   */
  private recalculateVisibilities(
    controlAndValues: Map<TypedControl, any>,
  ): void {
    const hiddenSet = new Set<string>();

    if (!this.visibilityFunction) {
      this.hiddenControlsNamesByFunctionSubject.next(hiddenSet);

      return;
    }

    for (const [control, value] of controlAndValues.entries()) {
      const isVisible = this.visibilityFunction(control, value);
      if (!isVisible) {
        hiddenSet.add(control.control.$name);
      }
    }
    this.hiddenControlsNamesByFunctionSubject.next(hiddenSet);
  }

  /**
   * Update control values to override control visibility
   *
   * @param newModel - changed form model
   */
  private updateControlValues(newModel: IFormModel): void {
    const controls = this.controlsAndValues.keys();
    for (const control of controls) {
      const controlKey = control.control.$name;
      const newValue = newModel[controlKey];
      this.controlsAndValues.set(control, newValue);
    }
  }

  /**
   * Check if page/field/control is hidden because of some condition
   *
   * @param name - page/field/control name
   */
  isHidden(name: string[] | string): boolean {
    const names = Array.isArray(name) ? name : [name];
    return names.some(n =>
      this.hiddenControlsNamesByFunctionSubject.value.has(n),
    );
  }

  /**
   * Stream of page/field/control hiding state by name
   *
   * @param name - page/field/control name
   */
  isHidden$(name: string[] | string): Observable<boolean> {
    const names = Array.isArray(name) ? name : [name];

    return this.hiddenControlsNamesByFunctionSubject.pipe(
      map(set => names.some(n => set.has(n))),
    );
  }

  /**
   * Add control hide conditions and disabled state to internal state
   *
   * @param control - control metadata
   * @param value - control current value
   */
  handleControl(control: TypedControl, value: string[] | string): void {
    this.controlsAndValues.set(control, value);
    this.recalculateVisibilities(this.controlsAndValues);
  }
}
